import React, { useEffect, useState } from 'react';
import { Row, Col, Card } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import apiService from '../services/api';
import qnaService from '../services/qnaService';

const StatCard = ({ label, icon, value, loading }) => (
  <div className="stat-card">
    <div>
      <div className="stat-card-label">{label}</div>
      <div className="stat-card-value">
        {loading ? (
          <span style={{ fontSize: '1rem', color: '#adb5bd' }}>...</span>
        ) : (
          value
        )}
      </div>
    </div>
    <div className="stat-card-icon">
      <i className={icon}></i>
    </div>
  </div>
);

const collections = [
  {
    name: 'الأسئلة والأجوبة',
    route: '/qna',
    icon: 'bi-chat-dots',
    description: 'إدارة أسئلة الشيوخ عبر واتساب',
    color: '#25D366',
  },
  {
    name: 'البث المباشر',
    route: '/live',
    icon: 'bi-broadcast',
    description: 'إدارة البث المباشر',
    color: '#dc3545',
  },
  {
    name: 'الفيديوهات',
    route: '/videos',
    icon: 'bi-camera-video',
    description: 'إدارة محتوى الفيديو',
    color: '#1B4D2A',
  },
  {
    name: 'الصوتيات',
    route: '/audios',
    icon: 'bi-mic-fill',
    description: 'إدارة التسجيلات الصوتية',
    color: '#C8A75A',
  },
  {
    name: 'التصنيفات',
    route: '/categories',
    icon: 'bi-folder',
    description: 'إدارة تصنيفات المحاضرات',
    color: '#6f42c1',
  },
  {
    name: 'الوسوم',
    route: '/collections/tags',
    icon: 'bi-tags',
    description: 'إدارة الوسوم',
    color: '#0dcaf0',
  },
  {
    name: 'القراء',
    route: '/readers',
    icon: 'bi-person-circle',
    description: 'إدارة الكتاب والقراء',
    color: '#fd7e14',
  },
  {
    name: 'الوسائط',
    route: '/collections/upload/files',
    icon: 'bi-images',
    description: 'إدارة ملفات الوسائط',
    color: '#20c997',
  },
];

const fetchCount = async (endpoint) => {
  const res = await apiService.getCollection(endpoint, {
    'pagination[pageSize]': 1,
    'pagination[page]': 1,
  });
  return res?.meta?.pagination?.total ?? '—';
};

const Dashboard = () => {
  const { user } = useAuth();
  const [counts, setCounts] = useState({ videos: null, audios: null, categories: null, qna: null });

  useEffect(() => {
    const load = async () => {
      const [videos, audios, categories] = await Promise.allSettled([
        fetchCount('mjalis-yts'),
        fetchCount('mjalis-vos'),
        fetchCount('types'),
      ]);

      setCounts((prev) => ({
        ...prev,
        videos:     videos.status     === 'fulfilled' ? videos.value     : '—',
        audios:     audios.status     === 'fulfilled' ? audios.value     : '—',
        categories: categories.status === 'fulfilled' ? categories.value : '—',
      }));

      try {
        const qnaData = await qnaService.getAll();
        const total = qnaData?.questions?.length ?? '—';
        setCounts((prev) => ({ ...prev, qna: total }));
      } catch {
        setCounts((prev) => ({ ...prev, qna: '—' }));
      }
    };

    load();
  }, []);

  const loading = counts.videos === null;

  const stats = [
    { label: 'الفيديوهات',       icon: 'bi-camera-video', value: counts.videos },
    { label: 'الصوتيات',         icon: 'bi-mic-fill',     value: counts.audios },
    { label: 'الأسئلة والأجوبة', icon: 'bi-chat-dots',    value: counts.qna },
    { label: 'التصنيفات',        icon: 'bi-folder',       value: counts.categories },
  ];

  const breadcrumbs = [{ label: 'الرئيسية', icon: 'bi-house-door' }];

  return (
    <Layout title="لوحة التحكم" breadcrumbs={breadcrumbs}>
      <div style={{ padding: '24px' }}>

        {/* Greeting */}
        <div className="mb-4">
          <h4 style={{ fontWeight: 700, color: '#1E1E1E', marginBottom: 4 }}>
            مرحباً بعودتك، {user?.username || user?.email}
          </h4>
          <p style={{ color: '#6c757d', fontSize: '0.9rem', margin: 0 }}>
            إدارة محتوى مسجد الموسوي من هنا
          </p>
        </div>

        {/* Stat cards */}
        <Row className="g-3 mb-4">
          {stats.map((s) => (
            <Col key={s.label} xs={12} sm={6} xl={3}>
              <StatCard {...s} loading={loading} />
            </Col>
          ))}
        </Row>

        {/* Section divider */}
        <div className="mb-4" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ height: 2, flex: 1, background: 'linear-gradient(to left, #C8A75A, transparent)', borderRadius: 2 }} />
          <span style={{ color: '#C8A75A', fontSize: '0.8rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
            الأقسام الرئيسية
          </span>
          <span style={{ height: 2, flex: 1, background: 'linear-gradient(to right, #C8A75A, transparent)', borderRadius: 2 }} />
        </div>

        {/* Collection cards */}
        <Row className="g-3">
          {collections.map((c) => (
            <Col key={c.route} xs={12} sm={6} lg={4} xl={3}>
              <Link to={c.route} className="text-decoration-none">
                <Card className="h-100 shadow-sm hover-card border-0">
                  <Card.Body className="d-flex flex-column align-items-center justify-content-center p-4">
                    <div
                      className="icon-wrapper mb-3"
                      style={{
                        width: 72,
                        height: 72,
                        backgroundColor: c.color + '18',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <i className={c.icon} style={{ fontSize: '2.2rem', color: c.color }} />
                    </div>
                    <Card.Title className="text-center mb-1 fw-bold" style={{ color: '#1E1E1E', fontSize: '0.95rem' }}>
                      {c.name}
                    </Card.Title>
                    <Card.Text className="text-center text-muted" style={{ fontSize: '0.78rem', margin: 0 }}>
                      {c.description}
                    </Card.Text>
                  </Card.Body>
                </Card>
              </Link>
            </Col>
          ))}
        </Row>
      </div>
    </Layout>
  );
};

export default Dashboard;
